import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ImageOff, Upload, FileText, RefreshCw } from 'lucide-react';
import * as installationApi from '@/api/installation.api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PageHeader } from '@/components/common/PageHeader';
import { MensajesTab } from '@/components/installation/MensajesTab';
import { GlosarioTab } from '@/components/installation/GlosarioTab';
import { TrabajosTab } from '@/components/installation/TrabajosTab';
import { fromConfig, toPayload, type Draft } from '@/lib/installation-draft';
import { useToast } from '@/hooks/useToast';

/**
 * Administración de la CONFIGURACIÓN DE LA INSTALACIÓN (solo ADMIN): datos del
 * negocio, persona/prompt del agente, catálogo con precios, reglas de CRM y
 * plantillas de mensajes. Es la contraparte visual del des-hardcodeo: lo que
 * antes vivía en código para el negocio aquí se ve y se edita como dato.
 * Básica a propósito: un borrador local + un único "Guardar" (PUT completo).
 */

/** Nombre base (es) de cada estado del lead, para no mostrar el enum crudo. */
const LEAD_STATUS_BASE_LABELS: Record<string, string> = {
  CALIENTE: 'Lead caliente',
  TIBIO: 'Lead tibio',
  FRIO: 'Lead frío',
  CERRADO: 'Cerrado (venta)',
  NO_CERRADO: 'No cerrado',
};


export function InstallationConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['installation', 'config'],
    queryFn: installationApi.getInstallationConfig,
  });
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (data) setDraft(fromConfig(data));
  }, [data]);

  // Actualiza el borrador (no-op mientras aún no cargó).
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const save = useMutation({
    mutationFn: () => installationApi.saveInstallationConfig(toPayload(draft as Draft)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('Configuración guardada — el PDF del catálogo se está regenerando');
    },
    onError: () => toast.error('No se pudo guardar la configuración'),
  });

  const logoUpload = useMutation({
    mutationFn: (file: File) => installationApi.uploadLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('Logo actualizado');
    },
    onError: () => toast.error('No se pudo subir el logo'),
  });

  const rowImageUpload = useMutation({
    mutationFn: async ({ file, index }: { file: File; index: number }) => ({
      index,
      stored: await installationApi.uploadCatalogImage(file),
    }),
    onSuccess: ({ index, stored }) => {
      if (!draft) return;
      const row = draft.catalog[index];
      if (!row) return;
      // La recién subida va PRIMERA: así se previsualiza de inmediato aunque la
      // fila ya arrastre claves viejas (algunas del seed apuntan a archivos que
      // no existen y darían "sin foto").
      const keys = row.imagenes ? `${stored.key}, ${row.imagenes}` : stored.key;
      set({ catalog: replaceAt(draft.catalog, index, { ...row, imagenes: keys }) });
      toast.success('Foto subida — recuerda Guardar cambios');
    },
    onError: () => toast.error('No se pudo subir la foto'),
  });

  const regenPdf = useMutation({
    mutationFn: installationApi.regenerateCatalogPdf,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('PDF del catálogo regenerado');
    },
    onError: () => toast.error('No se pudo generar el PDF'),
  });

  // "Quitar rotas": consulta al SERVIDOR qué claves de la fila no tienen archivo
  // (fuente de verdad; el onError del <img> mentiría si /media está caído) y las
  // remueve del borrador. El usuario todavía tiene que Guardar.
  const cleanBroken = useMutation({
    mutationFn: async ({ index, keys }: { index: number; keys: string[] }) => ({
      index,
      result: await installationApi.checkCatalogImages(keys),
    }),
    onSuccess: ({ index, result }) => {
      if (!draft) return;
      const row = draft.catalog[index];
      if (!row) return;
      if (result.missing.length === 0) {
        toast.success('Todas las fotos de la fila tienen archivo');
        return;
      }
      const missing = new Set(result.missing);
      const kept = imageKeys(row.imagenes).filter((k) => !missing.has(k));
      set({ catalog: replaceAt(draft.catalog, index, { ...row, imagenes: kept.join(', ') }) });
      toast.success(
        `${result.missing.length} foto(s) sin archivo quitada(s) — recuerda Guardar cambios`,
      );
    },
    onError: () => toast.error('No se pudo verificar las fotos'),
  });

  if (isError) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Instalación" description="Configuración del cliente de esta instalación." />
        <div className="space-y-3 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">No se pudo cargar la configuración.</p>
          <Button variant="outline" onClick={() => void refetch()}>Reintentar</Button>
        </div>
      </div>
    );
  }
  if (isLoading || !draft) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Instalación" description="Configuración del cliente de esta instalación." />
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={`Instalación · ${draft.businessName || 'sin nombre'}`}
        helpKey="installation"
        description="Todo lo específico del cliente vive aquí como dato: negocio, agente, catálogo, CRM y mensajes."
        action={
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Guardar cambios
          </Button>
        }
      />

      <Tabs defaultValue="negocio">
        <TabsList>
          <TabsTrigger value="negocio">Negocio</TabsTrigger>
          <TabsTrigger value="agente">Agente</TabsTrigger>
          <TabsTrigger value="catalogo">Catálogo</TabsTrigger>
          <TabsTrigger value="crm">CRM</TabsTrigger>
          <TabsTrigger value="mensajes">Mensajes</TabsTrigger>
          <TabsTrigger value="glosario">Glosario</TabsTrigger>
          <TabsTrigger value="trabajos">Trabajos</TabsTrigger>
        </TabsList>

        <TabsContent value="negocio">
          <Card>
            <CardHeader>
              <CardTitle>Datos del negocio</CardTitle>
              <CardDescription>Identidad comercial y reglas operativas básicas.</CardDescription>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2">
              <Input
                label="Nombre del negocio"
                value={draft.businessName}
                onChange={(e) => set({ businessName: e.target.value })}
              />
              <Input
                label="Prefijo de moneda"
                value={draft.currencyPrefix}
                onChange={(e) => set({ currencyPrefix: e.target.value })}
                placeholder="RD$"
              />
              <Input
                label="SLA del técnico en sitio (horas)"
                type="number"
                min={1}
                value={draft.slaHours}
                onChange={(e) => set({ slaHours: e.target.value })}
              />
              <Input
                label="Notion database ID (opcional)"
                value={draft.notionDatabaseId}
                onChange={(e) => set({ notionDatabaseId: e.target.value })}
                placeholder="Vacío = usar el del entorno"
              />
              <div className="md:col-span-2">
                <p className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Logo del negocio
                </p>
                <div className="flex items-center gap-3">
                  <MediaThumb mediaKey={data?.logoKey ?? null} alt="Logo del negocio" />
                  <div className="space-y-1">
                    <UploadImageButton
                      label={data?.logoKey ? 'Cambiar logo' : 'Subir logo'}
                      loading={logoUpload.isPending}
                      onFile={(f) => logoUpload.mutate(f)}
                    />
                    <p className="text-xs text-slate-400">
                      Sale en el PDF del catálogo que el asistente envía por WhatsApp.
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="agente">
          <Card>
            <CardHeader>
              <CardTitle>Persona del agente</CardTitle>
              <CardDescription>
                Identidad, modelo y prompt del agente AI de esta instalación. Ojo: el seed
                actualiza el Agent en base a esta config.
              </CardDescription>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nombre"
                  value={draft.agent.name}
                  onChange={(e) => set({ agent: { ...draft.agent, name: e.target.value } })}
                />
                <Input
                  label="Modelo"
                  value={draft.agent.model}
                  onChange={(e) => set({ agent: { ...draft.agent, model: e.target.value } })}
                />
                <Input
                  label="Máx. tokens"
                  type="number"
                  value={draft.agent.maxTokens}
                  onChange={(e) => set({ agent: { ...draft.agent, maxTokens: e.target.value } })}
                />
                <Input
                  label="Temperatura (0–1)"
                  type="number"
                  step="0.1"
                  min={0}
                  max={1}
                  value={draft.agent.temperature}
                  onChange={(e) => set({ agent: { ...draft.agent, temperature: e.target.value } })}
                />
              </div>
              <Textarea
                label="Descripción"
                rows={2}
                value={draft.agent.description}
                onChange={(e) => set({ agent: { ...draft.agent, description: e.target.value } })}
              />
              <Textarea
                label="Instrucciones del asistente (prompt)"
                rows={18}
                className="font-mono text-xs"
                value={draft.agent.systemPrompt}
                onChange={(e) => set({ agent: { ...draft.agent, systemPrompt: e.target.value } })}
              />
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="catalogo">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>Catálogo y precios</CardTitle>
                  <CardDescription>
                    Modelos que el agente cotiza, con su foto. El PDF del menú se regenera
                    solo al guardar; regenéralo a mano si acabas de subir fotos.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {data?.catalogPdfKey && (
                    <a href={`/media/${data.catalogPdfKey}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm">
                        <FileText className="mr-1 h-3.5 w-3.5" /> Ver PDF
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    loading={regenPdf.isPending}
                    onClick={() => regenPdf.mutate()}
                  >
                    <RefreshCw className="mr-1 h-3.5 w-3.5" /> Regenerar PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              {draft.catalog.map((row, i) => (
                <div
                  key={i}
                  className="grid gap-2 rounded-md border border-surface-border p-3 dark:border-slate-800 md:grid-cols-[auto_1fr_7rem_1fr_1fr_1fr_auto]"
                >
                  <div className={`flex flex-col items-center gap-1.5 ${i === 0 ? 'md:pt-7' : ''}`}>
                    <MediaThumbs csvKeys={row.imagenes} alt={row.name || 'producto'} />
                    <UploadImageButton
                      label="Foto"
                      loading={rowImageUpload.isPending && rowImageUpload.variables?.index === i}
                      onFile={(f) => rowImageUpload.mutate({ file: f, index: i })}
                    />
                    {imageKeys(row.imagenes).length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Quita de esta fila las claves de foto cuyo archivo no existe en el servidor"
                        loading={cleanBroken.isPending && cleanBroken.variables?.index === i}
                        onClick={() => cleanBroken.mutate({ index: i, keys: imageKeys(row.imagenes) })}
                      >
                        <ImageOff className="mr-1 h-3.5 w-3.5" /> Quitar rotas
                      </Button>
                    )}
                  </div>
                  <Input
                    label={i === 0 ? 'Modelo' : undefined}
                    placeholder="Modelo"
                    value={row.name}
                    onChange={(e) => set({ catalog: replaceAt(draft.catalog, i, { ...row, name: e.target.value }) })}
                  />
                  <Input
                    label={i === 0 ? 'Precio' : undefined}
                    placeholder="Precio"
                    type="number"
                    value={row.price}
                    onChange={(e) => set({ catalog: replaceAt(draft.catalog, i, { ...row, price: e.target.value }) })}
                  />
                  <Input
                    label={i === 0 ? 'Nota' : undefined}
                    placeholder="Nota (opcional)"
                    value={row.note}
                    onChange={(e) => set({ catalog: replaceAt(draft.catalog, i, { ...row, note: e.target.value }) })}
                  />
                  <Input
                    label={i === 0 ? 'Alias (coma)' : undefined}
                    placeholder="Alias, otro alias"
                    value={row.aliases}
                    onChange={(e) => set({ catalog: replaceAt(draft.catalog, i, { ...row, aliases: e.target.value }) })}
                  />
                  <Input
                    label={i === 0 ? 'Imágenes (coma)' : undefined}
                    placeholder="catalog/slug.jpg"
                    value={row.imagenes}
                    onChange={(e) => set({ catalog: replaceAt(draft.catalog, i, { ...row, imagenes: e.target.value }) })}
                  />
                  <div className={i === 0 ? 'md:pt-7' : ''}>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Quitar modelo"
                      onClick={() => set({ catalog: draft.catalog.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="md:col-span-full">
                    <Textarea
                      label={i === 0 ? 'Descripción (va al PDF y al agente)' : undefined}
                      placeholder="Descripción de venta: para quién es, qué lo hace especial (opcional)"
                      rows={2}
                      value={row.descripcion}
                      onChange={(e) =>
                        set({ catalog: replaceAt(draft.catalog, i, { ...row, descripcion: e.target.value }) })
                      }
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="secondary"
                onClick={() =>
                  set({
                    catalog: [
                      ...draft.catalog,
                      { name: '', price: '', note: '', descripcion: '', aliases: '', imagenes: '' },
                    ],
                  })
                }
              >
                <Plus className="mr-1 h-4 w-4" /> Añadir modelo
              </Button>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="crm">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Etiquetas de temperatura</CardTitle>
                <CardDescription>Cómo se llama cada estado del lead en el panel y en Notion.</CardDescription>
              </CardHeader>
              <CardBody className="grid gap-4 md:grid-cols-3">
                {Object.entries(draft.leadStatusLabels).map(([key, label]) => (
                  <Input
                    key={key}
                    label={LEAD_STATUS_BASE_LABELS[key] ?? key}
                    value={label}
                    onChange={(e) =>
                      set({ leadStatusLabels: { ...draft.leadStatusLabels, [key]: e.target.value } })
                    }
                  />
                ))}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Campos editables de la ficha</CardTitle>
                <CardDescription>
                  Datos del lead que un humano corrige a mano en la ficha. La clave debe
                  coincidir con la que captura el asistente (p. ej. zona, modelo_interes).
                </CardDescription>
              </CardHeader>
              <CardBody className="space-y-3">
                {draft.editableFields.map((f, i) => (
                  <div key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                    <Input
                      placeholder="clave (ej. zona)"
                      value={f.key}
                      onChange={(e) =>
                        set({ editableFields: replaceAt(draft.editableFields, i, { ...f, key: e.target.value }) })
                      }
                    />
                    <Input
                      placeholder="Etiqueta"
                      value={f.label}
                      onChange={(e) =>
                        set({ editableFields: replaceAt(draft.editableFields, i, { ...f, label: e.target.value }) })
                      }
                    />
                    <Input
                      placeholder="Placeholder"
                      value={f.placeholder}
                      onChange={(e) =>
                        set({
                          editableFields: replaceAt(draft.editableFields, i, { ...f, placeholder: e.target.value }),
                        })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Quitar campo"
                      onClick={() => set({ editableFields: draft.editableFields.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() =>
                    set({ editableFields: [...draft.editableFields, { key: '', label: '', placeholder: '' }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Añadir campo
                </Button>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Zonas y tipos de servicio</CardTitle>
                <CardDescription>Opciones de los selects (separadas por coma). Se usan también en Notion.</CardDescription>
              </CardHeader>
              <CardBody className="grid gap-4 md:grid-cols-2">
                <Input label="Zonas" value={draft.zonas} onChange={(e) => set({ zonas: e.target.value })} />
                <Input
                  label="Tipos de servicio"
                  value={draft.tiposServicio}
                  onChange={(e) => set({ tiposServicio: e.target.value })}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Centros de operación</CardTitle>
                <CardDescription>
                  Bases del negocio (ej. Santo Domingo, Punta Cana). Se muestran en el mapa de
                  Tracking y se marca si cada técnico está dentro o fuera de una base, con la
                  distancia a la más cercana.
                </CardDescription>
              </CardHeader>
              <CardBody className="space-y-3">
                {draft.centros.map((c, i) => (
                  <div key={i} className="grid gap-2 md:grid-cols-[1.4fr_1fr_1fr_0.7fr_auto]">
                    <Input
                      placeholder="Nombre (ej. Santo Domingo)"
                      value={c.nombre}
                      onChange={(e) =>
                        set({ centros: replaceAt(draft.centros, i, { ...c, nombre: e.target.value }) })
                      }
                    />
                    <Input
                      placeholder="Latitud (ej. 18.4861)"
                      value={c.lat}
                      onChange={(e) => set({ centros: replaceAt(draft.centros, i, { ...c, lat: e.target.value }) })}
                    />
                    <Input
                      placeholder="Longitud (ej. -69.9312)"
                      value={c.lng}
                      onChange={(e) => set({ centros: replaceAt(draft.centros, i, { ...c, lng: e.target.value }) })}
                    />
                    <Input
                      placeholder="Radio km"
                      value={c.radioKm}
                      onChange={(e) =>
                        set({ centros: replaceAt(draft.centros, i, { ...c, radioKm: e.target.value }) })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Quitar centro"
                      onClick={() => set({ centros: draft.centros.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() =>
                    set({ centros: [...draft.centros, { nombre: '', lat: '', lng: '', radioKm: '5' }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Añadir centro
                </Button>
              </CardBody>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="trabajos">
          {/* Viaja con el «Guardar cambios» general de la página. */}
          <TrabajosTab
            value={draft.trabajos}
            onChange={(patch) => set({ trabajos: { ...draft.trabajos, ...patch } })}
          />
        </TabsContent>

        <TabsContent value="glosario">
          <GlosarioTab
            value={draft.glosario}
            onChange={(patch) => set({ glosario: { ...draft.glosario, ...patch } })}
          />
        </TabsContent>

        <TabsContent value="mensajes">
          {/* Rediseño (feature/rediseno-config): tarjetas por plantilla con su
              switch del ruteo integrado + números/destinos al final. Los textos
              viajan con el «Guardar cambios» general; el ruteo con su botón. */}
          <MensajesTab
            messages={draft.messages}
            onChange={(patch) => set({ messages: { ...draft.messages, ...patch } })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Reemplaza el elemento `i` de una lista (inmutable). */
function replaceAt<T>(list: T[], i: number, item: T): T[] {
  return list.map((x, j) => (j === i ? item : x));
}

/** Todas las claves de imagen de un CSV, en orden (sin vacías). */
function imageKeys(csvKeys: string): string[] {
  return csvKeys
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Muestra UNA miniatura por cada clave de la fila; placeholder si no hay ninguna. */
function MediaThumbs({ csvKeys, alt }: { csvKeys: string; alt: string }) {
  const keys = imageKeys(csvKeys);
  if (keys.length === 0) return <MediaThumb mediaKey={null} alt={alt} />;
  return (
    <div className="flex max-w-[8rem] flex-wrap justify-center gap-1">
      {keys.map((k, idx) => (
        <MediaThumb key={`${k}-${idx}`} mediaKey={k} alt={alt} size="h-12 w-12" />
      ))}
    </div>
  );
}

/**
 * Miniatura desde el storage de medios. Distingue dos casos para no mostrar una
 * miniatura rota:
 *  - sin clave  → "sin foto" (la fila no tiene imagen).
 *  - clave rota → "falta": hay clave pero el archivo no existe (p. ej. los
 *    placeholders del seed como `catalog/inverter-12k.jpg`). Ver README/nota de
 *    limpieza; se arregla editando "Imágenes (coma)" de la fila y Guardando.
 */
function MediaThumb({ mediaKey, alt, size = 'h-14 w-14' }: { mediaKey: string | null; alt: string; size?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [mediaKey]);
  if (!mediaKey || broken) {
    const missing = Boolean(mediaKey); // hay clave, pero el archivo no está
    return (
      <div
        className={`flex ${size} shrink-0 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-surface-border bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-900`}
        title={missing ? `Falta el archivo: ${mediaKey}` : 'Sin foto'}
      >
        <ImageOff className="h-4 w-4" />
        <span className="text-[9px]">{missing ? 'falta' : 'sin foto'}</span>
      </div>
    );
  }
  return (
    <img
      src={`/media/${mediaKey}`}
      alt={alt}
      onError={() => setBroken(true)}
      className={`${size} shrink-0 rounded-md border border-surface-border object-cover dark:border-slate-700`}
    />
  );
}

/** Botón que abre el selector de archivo y sube una imagen. */
function UploadImageButton({
  label,
  onFile,
  loading,
}: {
  label: string;
  onFile: (file: File) => void;
  loading?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = ''; // permite re-subir el mismo archivo
        }}
      />
      <Button variant="outline" size="sm" loading={loading} onClick={() => inputRef.current?.click()}>
        <Upload className="mr-1 h-3.5 w-3.5" /> {label}
      </Button>
    </>
  );
}
